import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useApp } from '../../context/AppContext';
import { PageHeader, Button, Card, Modal, FormField, Input, Select, Textarea, GoldDivider } from '../ui';
import { Plus, Trash2, Settings, Bell, FileText, Megaphone, AlertTriangle, Info, CheckCircle } from 'lucide-react';
import { Navigate } from 'react-router-dom';

const SECTION_CONTENT_DEFAULT = {
  heroTitle: 'Welcome to UC Elites',
  heroSubtitle: 'Developing elite athletes on and off the field. Register your team for our upcoming tournaments and summer league.',
  aboutTitle: 'About UC Elites',
  aboutBody: 'UC Elites is a premier 7v7 youth football club based in Utah. We are dedicated to developing players of all skill levels through competitive tournaments, structured season leagues, and elite coaching.',
  contactEmail: 'info@ucelites.com',
  contactPhone: '(614) 555-0100',
  contactAddress: 'Utah',
  registrationNote: 'Registration closes May 15 for Summer Classic. Limited spots available per age group.',
  footerText: '© 2025 UC Elites. All rights reserved.',
};

export default function AdminEditor() {
  const { isAdmin } = useAuth();
  const { announcements } = useApp();
  const [content, setContent] = useState(SECTION_CONTENT_DEFAULT);
  const [notices, setNotices] = useState([
    { id: 'n1', type: 'warning', title: 'Registration closes May 15', body: 'Don\'t miss the Summer Classic deadline. Complete your registration and payment today.', active: true },
    { id: 'n2', type: 'info', title: 'Field assignments posted', body: 'June 7 field assignments are live in the Schedule section.', active: true },
    { id: 'n3', type: 'success', title: 'New: Team Store open', body: 'Order official UC Elites gear — jerseys, hoodies, bags and more.', active: false },
  ]);
  const [showAddNotice, setShowAddNotice] = useState(false);
  const [noticeForm, setNoticeForm] = useState({ type: 'info', title: '', body: '', active: true });
  const [saved, setSaved] = useState(false);
  const [activeTab, setActiveTab] = useState('content');

  if (!isAdmin) return <Navigate to="/dashboard" replace />;

  const updateContent = (key, val) => setContent(p => ({ ...p, [key]: val }));

  const saveContent = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const addNotice = (e) => {
    e.preventDefault();
    setNotices(p => [...p, { ...noticeForm, id: 'n' + Date.now() }]);
    setShowAddNotice(false);
    setNoticeForm({ type: 'info', title: '', body: '', active: true });
  };

  const toggleNotice = (id) => setNotices(p => p.map(n => n.id === id ? { ...n, active: !n.active } : n));
  const deleteNotice = (id) => setNotices(p => p.filter(n => n.id !== id));

  const NOTICE_ICONS = { info: <Info size={14} />, warning: <AlertTriangle size={14} />, success: <CheckCircle size={14} /> };
  const NOTICE_STYLES = {
    info:    { bg: 'rgba(30,70,160,0.2)', color: '#80b0f8', border: 'rgba(128,176,248,0.3)' },
    warning: { bg: 'rgba(230,172,0,0.12)', color: 'var(--gold-main)', border: 'rgba(230,172,0,0.3)' },
    success: { bg: 'rgba(40,120,60,0.2)', color: '#6eca7c', border: 'rgba(110,202,124,0.35)' },
  };

  const TABS = [
    { id: 'content', label: 'Page Content', icon: <FileText size={14} /> },
    { id: 'notices', label: 'Notices & Alerts', icon: <Bell size={14} /> },
    { id: 'settings', label: 'Club Settings', icon: <Settings size={14} /> },
  ];

  return (
    <div>
      <PageHeader title="Site Editor"
        subtitle="Edit page content, notices, and club settings visible to all users"
        actions={
          <Button variant="primary" onClick={saveContent}>
            {saved ? '✓ Saved!' : 'Save changes'}
          </Button>
        }
      />

      {/* Admin info banner */}
      <div style={{ background: 'linear-gradient(135deg, #0a0a0a, #1a1a1a)', border: '1px solid rgba(230,172,0,0.2)', borderRadius: 10, padding: '10px 16px', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ color: 'var(--gold-main)' }}><Settings size={16} /></div>
        <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)' }}>
          <strong style={{ color: 'var(--gold-main)' }}>Admin Editor:</strong> Changes here update content visible to coaches and parents across the portal. In production, these would persist to a database.
        </p>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 2, marginBottom: '1.5rem', background: 'var(--surface-2)', borderRadius: 10, padding: 4, width: 'fit-content' }}>
        {TABS.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 16px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 700, background: activeTab === tab.id ? 'var(--surface-0)' : 'transparent', color: activeTab === tab.id ? 'var(--text-primary)' : 'var(--text-muted)', boxShadow: activeTab === tab.id ? 'var(--shadow-sm)' : 'none', transition: 'all 0.15s' }}>
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {/* Page Content tab */}
      {activeTab === 'content' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem' }}>
          <Card>
            <h3 style={{ fontSize: 14, fontWeight: 800, fontFamily: 'var(--font-condensed)', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Megaphone size={14} color="var(--gold-main)" /> Hero Banner
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <FormField label="Hero headline">
                <Input value={content.heroTitle} onChange={e => updateContent('heroTitle', e.target.value)} />
              </FormField>
              <FormField label="Hero subtitle">
                <Textarea value={content.heroSubtitle} onChange={e => updateContent('heroSubtitle', e.target.value)} rows={3} />
              </FormField>
              <FormField label="Registration notice">
                <Input value={content.registrationNote} onChange={e => updateContent('registrationNote', e.target.value)} />
              </FormField>
            </div>
          </Card>

          <Card>
            <h3 style={{ fontSize: 14, fontWeight: 800, fontFamily: 'var(--font-condensed)', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: 6 }}>
              <FileText size={14} color="var(--gold-main)" /> About Section
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <FormField label="Section title">
                <Input value={content.aboutTitle} onChange={e => updateContent('aboutTitle', e.target.value)} />
              </FormField>
              <FormField label="About text">
                <Textarea value={content.aboutBody} onChange={e => updateContent('aboutBody', e.target.value)} rows={5} />
              </FormField>
            </div>
          </Card>

          <Card>
            <h3 style={{ fontSize: 14, fontWeight: 800, fontFamily: 'var(--font-condensed)', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Settings size={14} color="var(--gold-main)" /> Contact Info
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <FormField label="Email"><Input value={content.contactEmail} onChange={e => updateContent('contactEmail', e.target.value)} /></FormField>
              <FormField label="Phone"><Input value={content.contactPhone} onChange={e => updateContent('contactPhone', e.target.value)} /></FormField>
              <FormField label="Address"><Input value={content.contactAddress} onChange={e => updateContent('contactAddress', e.target.value)} /></FormField>
            </div>
          </Card>

          <Card>
            <h3 style={{ fontSize: 14, fontWeight: 800, fontFamily: 'var(--font-condensed)', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: 6 }}>
              <FileText size={14} color="var(--gold-main)" /> Footer
            </h3>
            <FormField label="Footer text">
              <Input value={content.footerText} onChange={e => updateContent('footerText', e.target.value)} />
            </FormField>
            {/* Preview */}
            <div style={{ marginTop: '1rem', background: 'linear-gradient(135deg, #0a0a0a, #111)', borderRadius: 8, padding: '1rem', border: '1px solid rgba(230,172,0,0.1)' }}>
              <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', textAlign: 'center', fontFamily: 'var(--font-condensed)', letterSpacing: '0.06em' }}>{content.footerText}</p>
            </div>
          </Card>
        </div>
      )}

      {/* Notices tab */}
      {activeTab === 'notices' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
            <Button variant="primary" onClick={() => setShowAddNotice(true)}><Plus size={14} /> Add notice</Button>
          </div>

          {/* Live preview */}
          <Card gold style={{ marginBottom: '1.5rem' }}>
            <h3 style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '1rem', textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: 'var(--font-condensed)' }}>Preview — Active notices shown to users</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {notices.filter(n => n.active).map(n => {
                const s = NOTICE_STYLES[n.type];
                return (
                  <div key={n.id} style={{ background: s.bg, border: `1px solid ${s.border}`, borderRadius: 8, padding: '10px 14px', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                    <span style={{ color: s.color, marginTop: 1 }}>{NOTICE_ICONS[n.type]}</span>
                    <div>
                      <span style={{ fontWeight: 700, fontSize: 13, color: s.color }}>{n.title}</span>
                      <span style={{ fontSize: 13, color: s.color, opacity: 0.8, marginLeft: 6 }}>{n.body}</span>
                    </div>
                  </div>
                );
              })}
              {notices.filter(n => n.active).length === 0 && (
                <p style={{ fontSize: 13, color: 'var(--text-muted)', textAlign: 'center', padding: '1rem' }}>No active notices</p>
              )}
            </div>
          </Card>

          {/* Notice list */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {notices.map(n => {
              const s = NOTICE_STYLES[n.type];
              return (
                <Card key={n.id} style={{ opacity: n.active ? 1 : 0.6 }}>
                  <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                    <div style={{ padding: '8px', background: s.bg, borderRadius: 8, border: `1px solid ${s.border}`, color: s.color, flexShrink: 0 }}>
                      {NOTICE_ICONS[n.type]}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--text-primary)', marginBottom: 3 }}>{n.title}</div>
                      <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{n.body}</p>
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
                      <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: n.active ? 'rgba(40,120,60,0.2)' : 'var(--surface-2)', color: n.active ? '#6eca7c' : 'var(--text-muted)', border: `1px solid ${n.active ? 'rgba(110,202,124,0.35)' : 'var(--border)'}` }}>
                        {n.active ? 'Active' : 'Hidden'}
                      </span>
                      <button onClick={() => toggleNotice(n.id)}
                        style={{ fontSize: 12, fontWeight: 700, padding: '4px 10px', borderRadius: 6, border: '1px solid var(--border-dark)', background: 'var(--surface-0)', cursor: 'pointer', color: 'var(--text-secondary)' }}>
                        {n.active ? 'Hide' : 'Show'}
                      </button>
                      <button onClick={() => deleteNotice(n.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#e57373', padding: 4, display: 'flex', borderRadius: 4 }}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Settings tab */}
      {activeTab === 'settings' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem' }}>
          <Card>
            <h3 style={{ fontSize: 14, fontWeight: 800, fontFamily: 'var(--font-condensed)', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: '1rem' }}>Registration settings</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <FormField label="Registration deadline">
                <Input type="date" defaultValue="2025-05-15" />
              </FormField>
              <FormField label="Default fee ($)">
                <Input type="number" defaultValue="350" />
              </FormField>
              <FormField label="Max teams per age group">
                <Input type="number" defaultValue="8" />
              </FormField>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
                <input type="checkbox" defaultChecked />
                <span>Allow waitlist after max teams reached</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
                <input type="checkbox" defaultChecked />
                <span>Require waiver before registration</span>
              </label>
            </div>
          </Card>

          <Card>
            <h3 style={{ fontSize: 14, fontWeight: 800, fontFamily: 'var(--font-condensed)', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: '1rem' }}>Payment settings</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <FormField label="Stripe publishable key" hint="sk_... key from your Stripe dashboard">
                <Input type="password" defaultValue="pk_test_••••••••••••••••" />
              </FormField>
              <FormField label="Payment due (days after registration)">
                <Input type="number" defaultValue="7" />
              </FormField>
              <FormField label="Late fee ($)">
                <Input type="number" defaultValue="25" />
              </FormField>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
                <input type="checkbox" defaultChecked />
                <span>Accept manual payments (cash/check)</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
                <input type="checkbox" defaultChecked />
                <span>Auto-send payment reminders</span>
              </label>
            </div>
          </Card>

          <Card>
            <h3 style={{ fontSize: 14, fontWeight: 800, fontFamily: 'var(--font-condensed)', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: '1rem' }}>Email notifications</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {[
                'Registration confirmation',
                'Payment received',
                'Payment overdue reminder',
                'Schedule published',
                'Score updates',
                'Broadcast messages',
              ].map(item => (
                <label key={item} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
                  <input type="checkbox" defaultChecked />
                  <span>{item}</span>
                </label>
              ))}
            </div>
          </Card>

          <Card>
            <h3 style={{ fontSize: 14, fontWeight: 800, fontFamily: 'var(--font-condensed)', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: '1rem' }}>User access</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {[
                { label: 'Coaches can edit rosters', checked: true },
                { label: 'Coaches can add players', checked: true },
                { label: 'Parents can message coaches', checked: true },
                { label: 'Parents can view payments', checked: false },
                { label: 'Public registration form', checked: true },
              ].map(item => (
                <label key={item.label} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
                  <input type="checkbox" defaultChecked={item.checked} />
                  <span>{item.label}</span>
                </label>
              ))}
            </div>
          </Card>
        </div>
      )}

      {/* Add Notice Modal */}
      <Modal open={showAddNotice} onClose={() => setShowAddNotice(false)} title="Add notice" width={460}>
        <form onSubmit={addNotice} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <FormField label="Notice type">
            <Select value={noticeForm.type} onChange={e => setNoticeForm(p=>({...p,type:e.target.value}))}>
              <option value="info">Info (blue)</option>
              <option value="warning">Warning (yellow)</option>
              <option value="success">Success (green)</option>
            </Select>
          </FormField>
          <FormField label="Title" required><Input value={noticeForm.title} onChange={e => setNoticeForm(p=>({...p,title:e.target.value}))} required /></FormField>
          <FormField label="Message" required><Textarea value={noticeForm.body} onChange={e => setNoticeForm(p=>({...p,body:e.target.value}))} rows={3} required /></FormField>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
            <input type="checkbox" checked={noticeForm.active} onChange={e => setNoticeForm(p=>({...p,active:e.target.checked}))} />
            <span>Active immediately</span>
          </label>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button type="button" variant="secondary" onClick={() => setShowAddNotice(false)}>Cancel</Button>
            <Button type="submit" variant="primary">Add notice</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
